import { useState } from 'react';
import BasicDataTable from '../../components/DataTable/BasicDataTable';
import BasicSearchComponent from '../../components/DataTable/BasicSearchComponent';
import ActivationModal from '../../components/Modals/ActivationModal';
import { useEconomicActivities } from '../../hooks/useEconomicActivities';
import EconomicActivityPopup from './EconomicActivityPopup';
import EditEconomicActivityPopup from './EditEconomicActivityPopup';

const EconomicActivityTable = () => {
  const {
    economicActivities,
    handlerSearchByType,
    toggleEconomicActivityStatus,
    handlerAddEconomicActivity,
  } = useEconomicActivities();

  // Estados para los modales
  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<{
    id: number;
    name: string;
    code: string;
    status: boolean;
  } | null>(null);
  const [isActivityPopupOpen, setIsActivityPopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);

  // 📌 Función para manejar el registro de actividades económicas
  const handleRegisterActivity = (activityData: {
    id?: number;
    name: string;
    code: string;
  }) => {
    console.log('Actividad económica registrada:', activityData);
    handlerAddEconomicActivity(activityData);
  };

  // 📌 Función para manejar la edición de actividades económicas
  const handleUpdateActivity = (activityData: {
    id: number;
    name: string;
    code: string;
  }) => {
    console.log('Actividad económica actualizada:', activityData);
    handlerAddEconomicActivity(activityData);
  };

  // 📌 Función para abrir el modal de edición con la actividad seleccionada
  const handleEditActivity = (activity: {
    id: number;
    name: string;
    code: string;
    status: boolean;
  }) => {
    console.log('Editando actividad económica:', activity);
    setSelectedActivity(activity);
    setIsEditPopupOpen(true);
  };

  // 📌 Columnas de la tabla
  const columns = [
    {
      Header: 'Código',
      accessor: 'code',
      Cell: ({ value }) => (
        <span className="font-mono font-semibold text-blue-600">{value}</span>
      ),
    },
    {
      Header: 'Nombre',
      accessor: 'name',
    },
    {
      Header: 'Estado',
      accessor: 'status',
      Cell: ({ value }) => (
        <span
          className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
            value ? 'bg-green-500 text-white' : 'bg-red text-white'
          }`}
        >
          {value ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
  ];

  // 📌 Botón para abrir el modal
  const addButton = (
    <button
      className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-white hover:bg-opacity-90"
      onClick={() => setIsActivityPopupOpen(true)}
    >
      + Nueva Actividad Económica
    </button>
  );

  // 📌 Función para manejar la activación/desactivación de la actividad económica
  const handleToggleActivityStatus = (activity: {
    id: number;
    name: string;
    code: string;
    status: boolean;
  }) => {
    setSelectedActivity(activity);
    setIsActivationModalOpen(true);
  };

  // 📌 Confirmar activación/desactivación
  const handleConfirmToggleStatus = () => {
    if (selectedActivity) {
      toggleEconomicActivityStatus(selectedActivity.id);
    }
    setIsActivationModalOpen(false);
  };

  // 📌 Cancelar activación/desactivación
  const handleCancelToggle = () => {
    setIsActivationModalOpen(false);
    setSelectedActivity(null);
  };

  // 📌 Acciones para la tabla
  const actions = [
    { key: 'edit', onClick: handleEditActivity },
    { key: 'toggle', onClick: handleToggleActivityStatus },
  ];

  return (
    <>
      <BasicDataTable
        columns={columns}
        data={economicActivities}
        noDataMessage="No hay actividades económicas disponibles."
        actions={actions}
        headerButton={addButton}
        searchComponent={
          <BasicSearchComponent onSearch={handlerSearchByType} />
        }
      />

      {/* Modal para activar/desactivar actividad económica */}
      <ActivationModal
        isOpen={isActivationModalOpen}
        title={
          selectedActivity?.status
            ? '¿Desactivar Actividad Económica?'
            : '¿Activar Actividad Económica?'
        }
        message={
          selectedActivity?.status
            ? '¿Estás seguro de que deseas desactivar esta actividad económica?'
            : '¿Estás seguro de que deseas activar esta actividad económica?'
        }
        confirmText={selectedActivity?.status ? 'Desactivar' : 'Activar'}
        cancelText="Cancelar"
        onConfirm={handleConfirmToggleStatus}
        onCancel={handleCancelToggle}
      />

      {/* Modal para agregar actividad económica */}
      <EconomicActivityPopup
        popupOpen={isActivityPopupOpen}
        setPopupOpen={setIsActivityPopupOpen}
        onRegister={handleRegisterActivity}
      />

      {/* Modal para editar actividad económica */}
      <EditEconomicActivityPopup
        popupOpen={isEditPopupOpen}
        setPopupOpen={setIsEditPopupOpen}
        onUpdate={handleUpdateActivity}
        selectedActivity={selectedActivity}
      />
    </>
  );
};

export default EconomicActivityTable;
